import React, { Fragment, useEffect, useMemo, useState } from 'react';
import StickyBox from 'react-sticky-box';
import Breadcrumb from '../Breadcrumb';
import Sidebar from '../Sidebar';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './PlanClassement.css';
import { PLAN_META_CREATE, PLAN_META_LIST } from '../../auth/urlParameters';
import { SimpleSpinner } from '../../../ui/Spinner';
import { ToastError, ToastConfirm, Alerte_Warning } from '../../../ui/AlertesUi';

const PlanClassementCreate = () => {
  const session = useSelector((state) => state.sessionAuth);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [planList, setPlanList] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [planForm, setPlanForm] = useState({
    code: '',
    title: '',
    description: '',
    version: '1.0',
    confidentialite: '',
    dureeConservationAnnee: '',
    parentId: searchParams.get('parent') || '',
    active: false,
  });

  const canManage = useMemo(() => {
    const roles = session.roles || [];
    return roles.includes('ROLE_ADMIN') || roles.includes('ROLE_SUPER_ADMIN') || roles.includes('ROLE_EDITED');
  }, [session.roles]);

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: 'Bearer ' + session.token },
    }),
    [session.token]
  );

  useEffect(() => {
    setLoadingPlans(true);
    axios
      .get(PLAN_META_LIST(), authHeaders)
      .then((res) => {
        const plans = res?.data?.plans || [];
        setPlanList(plans);
        setPlanForm((current) => ({
          ...current,
          parentId: plans.some((plan) => String(plan.id) === current.parentId) ? current.parentId : '',
        }));
        setLoadError(false);
      })
      .catch(() => { setPlanList([]); setLoadError(true); })
      .finally(() => setLoadingPlans(false));
  }, [authHeaders]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canManage) {
      ToastError("Vous n'êtes pas autorisé à créer un plan.");
      return;
    }

    const code = planForm.code.trim().toUpperCase();
    if (!/^[A-Z0-9]+(?:-[A-Z0-9]+)*$/.test(code) || code.length > 64) {
      ToastError('Code invalide : lettres, chiffres et tirets uniquement.');
      return;
    }
    if (planList.some((plan) => plan.code?.toUpperCase() === code)) {
      ToastError('Ce code de classement existe déjà.');
      return;
    }
    const payload = {
      ...planForm,
      code,
      title: planForm.title.trim(),
      description: planForm.description.trim(),
      version: planForm.version.trim(),
      dureeConservationAnnee: planForm.dureeConservationAnnee !== ''
        ? Number(planForm.dureeConservationAnnee)
        : null,
      parentId: planForm.parentId || null,
    };

    setSaving(true);
    axios
      .post(PLAN_META_CREATE(), payload, authHeaders)
      .then(() => {
        ToastConfirm('Plan créé');
        navigate('/apps/in/plan-classement');
      })
      .catch((err) => {
        ToastError(err.response?.data?.message || 'Erreur création plan');
      })
      .finally(() => setSaving(false));
  };

  return (
    <Fragment>
      <Breadcrumb title="Plan de classement" submenu="Création" />
      <div className="content">
        <div className="container">
          <div className="row">
            <div className="col-md-3 col-lg-3 col-xl-3 theiaStickySidebar">
              <StickyBox offsetTop={20} offsetBottom={20}>
                <Sidebar />
              </StickyBox>
            </div>
            <div className="col-md-8 col-lg-9 col-xl-9">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h4 className="htitle mb-0">Créer un plan de classement</h4>
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => navigate('/apps/in/plan-classement')}
                >
                  Retour à la liste
                </button>
              </div>

              {!canManage && Alerte_Warning("Vous n'êtes pas autorisé à créer un plan.")}
              {loadError && Alerte_Warning('La liste des plans est indisponible. Vérifiez la connexion avant de créer un plan.')}

              <div className="card">
                <div className="card-body">
                  {loadingPlans ? (
                    SimpleSpinner()
                  ) : (
                    <form onSubmit={handleSubmit}>
                      <p className="text-muted">Définissez une structure stable par fonction ou activité. Les classes et dossiers seront ajoutés après la création du plan.</p>
                      <div className="form-group">
                        <label htmlFor="planCode">Code de classement *</label>
                        <input
                          id="planCode"
                          className="form-control"
                          value={planForm.code}
                          onChange={(e) => setPlanForm({ ...planForm, code: e.target.value })}
                          required
                          maxLength={64}
                          placeholder="Ex. RH ou RH-CONTRATS"
                          disabled={saving || !canManage}
                        />
                        <small className="form-text text-muted">Code unique et stable : lettres, chiffres et tirets, sans espaces ni accents.</small>
                      </div>
                      <div className="form-group">
                        <label htmlFor="planTitle">Intitulé *</label>
                        <input
                          id="planTitle"
                          className="form-control"
                          value={planForm.title}
                          onChange={(e) => setPlanForm({ ...planForm, title: e.target.value })}
                          required
                          maxLength={255}
                          placeholder="Ex. Gestion des ressources humaines"
                          disabled={saving || !canManage}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="planDescription">Périmètre et contenu</label>
                        <textarea id="planDescription" className="form-control" rows={3}
                          value={planForm.description}
                          onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                          placeholder="Fonctions, activités et types de dossiers couverts par ce plan"
                          disabled={saving || !canManage} />
                        <small className="form-text text-muted">Précisez ce qui entre dans le plan pour faciliter le classement des archives.</small>
                      </div>
                      <div className="form-group">
                        <label htmlFor="planVersion">Version du plan *</label>
                        <input id="planVersion" className="form-control" value={planForm.version}
                          onChange={(e) => setPlanForm({ ...planForm, version: e.target.value })}
                          required maxLength={32} disabled={saving || !canManage} />
                      </div>
                      <div className="form-group plan-parent-field">
                        <label htmlFor="planParent">Plan parent</label>
                        <select id="planParent" className="form-control" value={planForm.parentId}
                          onChange={(e) => setPlanForm({ ...planForm, parentId: e.target.value })}
                          disabled={saving || !canManage || loadError}>
                          <option value="">Aucun (plan racine)</option>
                          {planList.map((plan) => <option key={plan.id} value={plan.id}>{plan.code} — {plan.title}</option>)}
                        </select>
                        <small className="form-text text-muted">Choisissez un plan existant pour créer une sous-partie de sa structure.</small>
                      </div>
                      <h5 className="mt-4">Indications générales de conservation</h5>
                      <p className="text-muted">Ces indications décrivent le plan. Elles ne remplacent pas une règle approuvée pour chaque catégorie de documents et ne déclenchent pas de destruction automatique.</p>
                      <div className="form-group">
                        <label htmlFor="planConfidentialite">Confidentialité par défaut</label>
                        <select
                          id="planConfidentialite"
                          className="form-control"
                          value={planForm.confidentialite}
                          onChange={(e) => setPlanForm({ ...planForm, confidentialite: e.target.value })}
                          disabled={saving || !canManage}
                        >
                          <option value="">Choisir</option>
                          <option value="Interne">Interne</option>
                          <option value="Restreint">Restreint</option>
                          <option value="Confidentiel">Confidentiel</option>
                          <option value="Très Confidentiel">Très Confidentiel</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor="planDuree">Durée de conservation par défaut (années)</label>
                        <input
                          id="planDuree"
                          className="form-control"
                          type="number"
                          min="0.01"
                          step="any"
                          value={planForm.dureeConservationAnnee}
                          onChange={(e) =>
                            setPlanForm({ ...planForm, dureeConservationAnnee: e.target.value })
                          }
                          disabled={saving || !canManage}
                        />
                        <small className="form-text text-muted">Ex. 5 ans ou 1,5 an. Laissez vide si aucune durée commune ne s’applique. Le point de départ et le sort final se définissent dans les règles de conservation des archives.</small>
                      </div>
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={planForm.active}
                          onChange={(e) => setPlanForm({ ...planForm, active: e.target.checked })}
                          id="planActive"
                          disabled={saving || !canManage}
                        />
                        <label className="form-check-label" htmlFor="planActive">
                          Activer le plan
                        </label>
                        <small className="form-text text-muted">Un plan actif est proposé pour les nouveaux dépôts. Activez-le après validation de sa structure et de ses règles.</small>
                      </div>
                      <button className="btn btn-primary" type="submit" disabled={saving || !canManage || loadError}>
                        {saving ? 'Enregistrement...' : 'Enregistrer'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default PlanClassementCreate;
